import React, { useEffect, useState } from "react";
import { Badge, Box, Button, Collapse, Flex, HStack, Input, Text, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import type { ApiRequestSpec } from "../flow/types";
import { executeRecorded } from "../flow/engine/apiClient";
import {
  clearSupplierSession,
  getSupplierLoginSecret,
  getSupplierSession,
  normalizeMobile,
  proxyLoginSupplier,
  requestOtp,
  saveManualSupplierSession,
  setSupplierLoginSecret,
  verifyOtpAsSupplier,
  type SupplierSession,
} from "../flow/engine/supplierSession";
import { extractApiErrorMessage } from "../flow/engine/errors";
import useNotification from "../../../utils/useNotification";
import { formatClock } from "./statusMeta";

interface Props {
  supplierId?: string;
  supplierName?: string;
  supplierMobile?: string;
  orcaUserMobile?: string;
  disabled: boolean;
}

const api = <T = any,>(spec: ApiRequestSpec) =>
  executeRecorded<T>(spec, { stepId: "supplier_session_panel", onRecord: () => undefined });

const SupplierSessionPanel: React.FC<Props> = ({ supplierId, supplierName, supplierMobile, orcaUserMobile, disabled }) => {
  const toast = useNotification();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<SupplierSession | undefined>();
  const [secret, setSecret] = useState(getSupplierLoginSecret());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [otpMode, setOtpMode] = useState<"role" | "supplier" | null>(null);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    setSession(supplierId ? getSupplierSession(supplierId) : undefined);
    setError(null);
    setOtpMode(null);
    setOtpSent(false);
    setOtp("");
  }, [supplierId]);

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (err: any) {
      setError(err?.message || extractApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const doProxyLogin = () =>
    run("proxy", async () => {
      if (!supplierId) return;
      setSupplierLoginSecret(secret);
      const s = await proxyLoginSupplier({ api, supplierId, mobileNumber: supplierMobile || "", secret });
      setSession(s);
      toast({ status: "success", description: `Supplier session ready (${s.userName || s.mobileNumber})` });
    });

  const otpTarget = otpMode === "role" ? orcaUserMobile : supplierMobile;

  const sendOtp = () =>
    run("otp-send", async () => {
      if (!otpTarget) throw new Error("No mobile number available for OTP");
      await requestOtp(api, otpTarget);
      setOtpSent(true);
      toast({ status: "success", description: `OTP sent to ${otpTarget}` });
    });

  const verifyOtp = () =>
    run("otp-verify", async () => {
      if (!otpTarget || !otp) throw new Error("Enter the OTP");
      const res = await verifyOtpAsSupplier(api, otpTarget, otp);
      if (otpMode === "supplier" && supplierId) {
        const s = saveManualSupplierSession({ supplierId, mobileNumber: otpTarget, token: res.data.auth, user: res.data.user });
        setSession(s);
        toast({ status: "success", description: "Supplier session stored for this browser session" });
      } else {
        toast({
          status: "success",
          description: "SUPPLIER_WEB_APP role enabled for your user. Log out and back in to OrCA so your token carries the new role, then use proxy login.",
        });
      }
      setOtpMode(null);
      setOtp("");
      setOtpSent(false);
    });

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <Flex px={4} py={2.5} justify="space-between" align="center" gap={3} cursor="pointer" onClick={() => setOpen((o) => !o)} _hover={{ bg: "gray.50" }}>
        <HStack spacing={2} minW={0} flex={1}>
          <Text fontWeight="700" fontSize="sm" flexShrink={0}>
            Supplier session
          </Text>
          {session ? (
            <Badge colorScheme="green" fontSize="9px" noOfLines={1} maxW="100%">
              {session.method} · {session.userName || session.mobileNumber}
            </Badge>
          ) : (
            <Badge colorScheme="gray" fontSize="9px" noOfLines={1}>
              auto on first supplier step
            </Badge>
          )}
        </HStack>
        <Text fontSize="xs" color="gray.500" flexShrink={0}>
          {open ? "Hide" : "Show"}
        </Text>
      </Flex>
      <Collapse in={open} animateOpacity>
        <VStack align="stretch" spacing={3} px={4} pb={4}>
          <Text fontSize="xs" color="gray.500">
            Supplier steps call the same APIs as the SupplierApp using a supplier JWT obtained via <code>/otp/verifyProxyUser</code> (requires your OrCA user to hold a
            SUPPLIER_WEB_APP admin role and the proxy secret). Enter the secret once — it is stored in this browser’s localStorage. The supplier JWT lives only in
            sessionStorage and is redacted in all recorded cURL.
          </Text>
          <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50" fontSize="xs" minW={0}>
            <Flex justify="space-between" align="flex-start" gap={2} mb={1.5}>
              <Text fontWeight="600" fontSize="sm" noOfLines={2} minW={0}>
                {supplierName || "No supplier selected"}
              </Text>
              {session && (
                <Badge colorScheme="green" variant="subtle" fontSize="9px" flexShrink={0}>
                  session active
                </Badge>
              )}
            </Flex>
            <VStack align="stretch" spacing={0.5} color="gray.500" fontFamily="mono">
              {supplierId && (
                <Text wordBreak="break-all" title={supplierId}>
                  {supplierId}
                </Text>
              )}
              <Text>{normalizeMobile(supplierMobile || "") || "no mobile"}</Text>
              {session && (
                <Text>
                  since {formatClock(session.obtainedAt)}
                </Text>
              )}
            </VStack>
          </Box>

          <VStack align="stretch" spacing={2}>
            <Box>
              <Text fontSize="10px" color="gray.500" mb={1}>
                Proxy-login secret (saved in this browser)
              </Text>
              <Input
                size="sm"
                type="password"
                value={secret}
                onChange={(e) => {
                  const next = e.target.value;
                  setSecret(next);
                  setSupplierLoginSecret(next);
                }}
                bg="white"
                placeholder="secretKey"
                autoComplete="off"
              />
            </Box>
            <Wrap spacing={2}>
              <WrapItem>
                <Button size="sm" colorScheme="purple" onClick={doProxyLogin} isLoading={busy === "proxy"} isDisabled={disabled || !supplierId}>
                  {session ? "Re-login" : "Proxy login now"}
                </Button>
              </WrapItem>
              {session && (
                <WrapItem>
                  <Button
                    size="sm"
                    variant="ghost"
                    isDisabled={disabled}
                    onClick={() => {
                      clearSupplierSession(supplierId);
                      setSession(undefined);
                    }}
                  >
                    Clear
                  </Button>
                </WrapItem>
              )}
            </Wrap>
          </VStack>

          <Wrap spacing={2}>
            <WrapItem>
              <Button size="xs" variant="outline" onClick={() => setOtpMode(otpMode === "role" ? null : "role")} isDisabled={disabled || !orcaUserMobile}>
                Enable supplier role (OTP on {orcaUserMobile || "your number"})
              </Button>
            </WrapItem>
            <WrapItem>
              <Button size="xs" variant="outline" onClick={() => setOtpMode(otpMode === "supplier" ? null : "supplier")} isDisabled={disabled || !supplierMobile}>
                Log in as supplier via OTP
              </Button>
            </WrapItem>
          </Wrap>

          <Collapse in={Boolean(otpMode)} animateOpacity>
            <Box borderWidth="1px" borderColor="purple.200" borderRadius="md" p={3}>
              <Text fontSize="xs" mb={2}>
                {otpMode === "role"
                  ? "One-time: verifying an OTP for your own number with role=SUPPLIER grants your user the SUPPLIER_WEB_APP role so proxy login works afterwards."
                  : `Fallback: request an OTP on the supplier's number (${supplierMobile}) and paste it here to obtain a supplier token directly.`}
              </Text>
              <Wrap spacing={2}>
                <WrapItem>
                  <Button size="sm" onClick={sendOtp} isLoading={busy === "otp-send"} isDisabled={disabled}>
                    {otpSent ? "Resend OTP" : "Send OTP"}
                  </Button>
                </WrapItem>
                <WrapItem>
                  <Input size="sm" w="120px" placeholder="OTP" value={otp} onChange={(e) => setOtp(e.target.value)} isDisabled={!otpSent} bg="white" />
                </WrapItem>
                <WrapItem>
                  <Button size="sm" colorScheme="purple" onClick={verifyOtp} isLoading={busy === "otp-verify"} isDisabled={disabled || !otpSent || !otp}>
                    Verify
                  </Button>
                </WrapItem>
              </Wrap>
            </Box>
          </Collapse>

          {error && (
            <Text fontSize="xs" color="red.600" whiteSpace="pre-wrap">
              {error}
            </Text>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default SupplierSessionPanel;
